import React from "react";
import { memo } from "react";
import { Box, Button, Chip, CircularProgress, TextField, Typography } from "@mui/material";
import { FaCopy, FaGlobe, FaPaperclip } from "react-icons/fa";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DownloadIcon from "@mui/icons-material/Download";
import DeleteIcon from "@mui/icons-material/Delete";
import { copiarTexto } from "../../utils/clipboard";

// ── Sub-componente: checklist de documentos de uma ficha ─────────────────────

export const ChecklistDocumentos = memo(function ChecklistDocumentos({
  checklist,
  fichaId,
  docs,
  uploadState,
  uploadando,
  ro,

  mostrarLinkGoogle = false,
  linkGoogleImovel = "",
  onChangeLinkGoogleImovel,

  observacaoDocumental = "",
  onChangeObservacaoDocumental,

  onSetUploadField,
  onUpload,
  onDelete,
  onVisualizar,
  onDownload,
}) {
  if (!fichaId) return null;

  const itens = checklist;
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, pt: 0.5 }}>
      {itens.map((item) => {
        const docsDoItem = docs.filter((d) => d.tipo === item.tipo);
        const temDoc = docsDoItem.length > 0;
        const uState = uploadState[item.tipo] ?? { arquivo: null, nome: "" };
        const chave = `${fichaId}_${item.tipo}`;
        const enviando = !!uploadando[chave];

        const corBorda = temDoc ? "#2e7d32" : "#444";

        return (
          <Box
            key={item.tipo}
            sx={{
              borderLeft: `3px solid ${corBorda}`,
              pl: 1.5,
              py: 1,
              backgroundColor: "var(--ig-f-252525)",
              borderRadius: "0 6px 6px 0",
            }}
          >
            {/* Cabeçalho do item */}
            <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 0.75, mb: 0.5 }}>
              <Typography sx={{ color: "var(--ig-t-ffffff)", fontWeight: 600, fontSize: "0.87rem", flex: 1 }}>{item.label}</Typography>
              {temDoc && (
                <Chip
                  label="✓ Enviado"
                  size="small"
                  sx={{
                    backgroundColor: "var(--ig-f-1a3e2a)",
                    color: "var(--ig-t-81c784)",
                    fontSize: "0.69rem",
                    height: 20,
                  }}
                />
              )}
            </Box>

            {/* Arquivos já enviados */}
            {docsDoItem.map((doc) => (
              <Box key={doc.id} sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.5, pl: 0.5 }}>
                <Typography sx={{ color: "var(--ig-t-bbbbbb)", fontSize: "0.79rem", flex: 1 }} noWrap title={doc.arquivo}>
                  📄 {doc.arquivo}
                </Typography>
                <Button
                  size="small"
                  startIcon={<VisibilityIcon sx={{ fontSize: 14 }} />}
                  onClick={() => onVisualizar(fichaId, doc.id)}
                  sx={{
                    backgroundColor: "#00897b",
                    color: "#fff",
                    fontSize: "0.71rem",
                    minWidth: 0,
                    px: 1.2,
                    width: "auto",
                    textTransform: "none",
                    "&:hover": { backgroundColor: "#00695c" },
                  }}
                >
                  Visualizar
                </Button>
                <Button
                  size="small"
                  startIcon={<DownloadIcon sx={{ fontSize: 14 }} />}
                  onClick={() => onDownload(fichaId, doc.id, doc.arquivo)}
                  sx={{
                    backgroundColor: "#1976d2",
                    color: "#fff",
                    fontSize: "0.71rem",
                    minWidth: 0,
                    px: 1.2,
                    width: "auto",
                    textTransform: "none",
                    "&:hover": { backgroundColor: "#1565c0" },
                  }}
                >
                  Baixar
                </Button>
                {!ro && (
                  <Button
                    size="small"
                    startIcon={<DeleteIcon sx={{ fontSize: 14 }} />}
                    onClick={() => onDelete(fichaId, doc.id)}
                    sx={{
                      backgroundColor: "#d32f2f",
                      color: "#fff",
                      fontSize: "0.71rem",
                      minWidth: 0,
                      px: 1.2,
                      width: "auto",
                      textTransform: "none",
                      "&:hover": { backgroundColor: "#c62828" },
                    }}
                  >
                    Excluir
                  </Button>
                )}
              </Box>
            ))}

            {/* Linha de upload (apenas modo edição) */}
            {!ro && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1, flexWrap: "wrap" }}>
                <input
                  type="file"
                  id={`file-${fichaId}-${item.tipo}`}
                  style={{ display: "none" }}
                  accept=".pdf,.jpg,.jpeg,.png,.xlsx,.doc,.docx"
                  onChange={(e) => {
                    const f = e.target.files[0];
                    if (f) onSetUploadField(fichaId, item.tipo, "arquivo", f);
                    e.target.value = "";
                  }}
                />
                <Button
                  size="small"
                  startIcon={<FaPaperclip />}
                  onClick={() => document.getElementById(`file-${fichaId}-${item.tipo}`)?.click()}
                  sx={{
                    backgroundColor: "#007bff",
                    color: "#fff",
                    fontSize: "0.74rem",
                    fontWeight: 700,
                    "&:hover": { backgroundColor: "#0069d9" },
                    textTransform: "none",
                    px: 1.5,
                    width: "auto",
                    maxWidth: 200,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                  title={uState.arquivo ? uState.arquivo.name : ""}
                >
                  {uState.arquivo ? uState.arquivo.name : "GED"}
                </Button>
                <TextField
                  size="small"
                  placeholder="Nome descritivo (opcional)"
                  value={uState.nome}
                  onChange={(e) => onSetUploadField(fichaId, item.tipo, "nome", e.target.value.toUpperCase())}
                  sx={{
                    flex: 1,
                    minWidth: 130,
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: "var(--ig-f-2a2a2a)",
                      color: "var(--ig-t-ffffff)",
                      "& fieldset": { borderColor: "var(--ig-b-444444)" },
                      "&:hover fieldset": { borderColor: "var(--ig-b-888888)" },
                    },
                    "& input": { color: "var(--ig-t-ffffff)", fontSize: "0.79rem", py: "5px", textTransform: "uppercase" },
                  }}
                />
                <Button
                  size="small"
                  onClick={() => onUpload(fichaId, item.tipo, item.obrigatorio)}
                  disabled={!uState.arquivo || enviando}
                  startIcon={enviando ? <CircularProgress size={12} sx={{ color: "var(--ig-t-ffffff)" }} /> : null}
                  sx={{
                    backgroundColor: "#1998a8",
                    color: "#fff",
                    fontSize: "0.74rem",
                    "&:hover": { backgroundColor: "#1565c0" },
                    "&.Mui-disabled": { backgroundColor: "var(--ig-f-333333)", color: "#555" },
                    textTransform: "none",
                    px: 1.5,
                    width: "auto",
                  }}
                >
                  {enviando ? "Enviando..." : "Enviar"}
                </Button>
                {uState.arquivo && (
                  <Button
                    size="small"
                    onClick={() => {
                      onSetUploadField(fichaId, item.tipo, "arquivo", null);
                      onSetUploadField(fichaId, item.tipo, "nome", "");
                    }}
                    disabled={enviando}
                    sx={{
                      backgroundColor: "#d32f2f",
                      color: "#fff",
                      fontSize: "0.74rem",
                      "&:hover": { backgroundColor: "#c62828" },
                      "&.Mui-disabled": { backgroundColor: "var(--ig-f-333333)", color: "#555" },
                      textTransform: "none",
                      px: 1.5,
                      width: "auto",
                    }}
                  >
                    Cancelar
                  </Button>
                )}
              </Box>
            )}
          </Box>
        );
      })}

      {mostrarLinkGoogle && (
        <Box
          sx={{
            borderLeft: "3px solid var(--ig-b-444444)",
            pl: 1.5,
            py: 1,
            backgroundColor: "var(--ig-f-252525)",
            borderRadius: "0 6px 6px 0",
          }}
        >
          <Box
            sx={{
              display: "flex",
              gap: 1,
              alignItems: "flex-start",
            }}
          >
            <TextField
              label="Link do Imóvel no Google Maps"
              size="small"
              fullWidth
              placeholder="https://www.google.com/maps/..."
              value={linkGoogleImovel || ""}
              slotProps={{
                input: {
                  readOnly: ro,
                },
                htmlInput: {
                  maxLength: 500,
                },
              }}
              onChange={(e) => {
                if (!ro) {
                  onChangeLinkGoogleImovel?.(e.target.value);
                }
              }}
              sx={{
                "& .MuiInputLabel-root": {
                  color: "var(--ig-t-ffffff)",
                  fontWeight: "bold",
                },

                "& .MuiOutlinedInput-root": {
                  backgroundColor: ro ? "var(--ig-f-252525)" : "var(--ig-f-2a2a2a)",
                  color: "var(--ig-t-ffffff)",

                  "& fieldset": {
                    borderColor: ro ? "var(--ig-b-333333)" : "var(--ig-b-444444)",
                  },

                  "&:hover fieldset": {
                    borderColor: ro ? "var(--ig-b-333333)" : "var(--ig-b-888888)",
                  },
                },

                "& input": {
                  color: "var(--ig-t-ffffff)",
                },
              }}
            />

            {linkGoogleImovel?.trim() && (
              <>
                <Button
                  variant="contained"
                  startIcon={<FaCopy />}
                  onClick={async () => {
                    const ok = await copiarTexto(linkGoogleImovel);

                    if (!ok) {
                      return;
                    }
                  }}
                  sx={{
                    width: "auto",
                    minWidth: 110,
                    height: 40,
                    mt: "2px",
                    backgroundColor: "#2196f3",

                    "&:hover": {
                      backgroundColor: "#1976d2",
                    },

                    fontWeight: "bold",
                    whiteSpace: "nowrap",
                  }}
                >
                  Copiar
                </Button>

                <Button
                  variant="contained"
                  startIcon={<FaGlobe />}
                  onClick={() => window.open(linkGoogleImovel, "_blank", "noopener,noreferrer")}
                  sx={{
                    width: "auto",
                    minWidth: 110,
                    height: 40,
                    mt: "2px",
                    backgroundColor: "#f97316",

                    "&:hover": {
                      backgroundColor: "#ea580c",
                    },

                    fontWeight: "bold",
                    whiteSpace: "nowrap",
                  }}
                >
                  Abrir
                </Button>
              </>
            )}
          </Box>
        </Box>
      )}

      <Box
        sx={{
          borderLeft: "3px solid var(--ig-b-444444)",
          pl: 1.5,
          py: 1,
          backgroundColor: "var(--ig-f-252525)",
          borderRadius: "0 6px 6px 0",
        }}
      >
        <Typography
          sx={{
            color: "var(--ig-t-ffffff)",
            fontWeight: 600,
            fontSize: "0.87rem",
            mb: 1,
          }}
        >
          Observação Documental
        </Typography>

        <TextField
          size="small"
          fullWidth
          multiline
          rows={3}
          value={observacaoDocumental || ""}
          onChange={(e) => !ro && onChangeObservacaoDocumental(e.target.value.toUpperCase())}
          slotProps={{
            input: {
              readOnly: ro,
            },
          }}
          sx={{
            "& .MuiOutlinedInput-root": {
              backgroundColor: ro ? "var(--ig-f-252525)" : "var(--ig-f-2a2a2a)",
              color: "var(--ig-t-ffffff)",
              "& fieldset": {
                borderColor: "var(--ig-b-444444)",
              },
            },
            "& textarea": {
              color: "var(--ig-t-ffffff)",
              textTransform: "uppercase",
            },
          }}
        />
      </Box>
    </Box>
  );
});
